/**
 * ExpressionEngine
 *
 * Motor de avaliacao de expressoes matematicas para colunas customizadas.
 * Suporta operadores, funcoes e variaveis.
 */

// Tipos de erro
export const ExpressionErrorType = {
    SYNTAX_ERROR: 'SYNTAX_ERROR',
    UNKNOWN_FUNCTION: 'UNKNOWN_FUNCTION',
    UNKNOWN_VARIABLE: 'UNKNOWN_VARIABLE',
    INVALID_ARGUMENTS: 'INVALID_ARGUMENTS',
    DIVISION_BY_ZERO: 'DIVISION_BY_ZERO',
    RUNTIME_ERROR: 'RUNTIME_ERROR',
};

// Mensagens de erro em PT-BR
const ERROR_MESSAGES = {
    SYNTAX_ERROR: 'Erro de sintaxe na expressao',
    UNKNOWN_FUNCTION: 'Funcao desconhecida: {function}',
    UNKNOWN_VARIABLE: 'Variavel desconhecida: {variable}',
    INVALID_ARGUMENTS: 'Argumentos invalidos para {function}. Esperado: {expected}',
    DIVISION_BY_ZERO: 'Divisao por zero detectada',
    RUNTIME_ERROR: 'Erro ao avaliar expressao: {error}',
};

class ExpressionEngine {
    constructor() {
        // Cache de resultados
        this.cache = new Map();

        // Timeout de execucao (ms)
        this.executionTimeout = 100;
    }

    /**
     * Valida sintaxe de uma expressao
     * @param {string} expression
     * @returns {{valid: boolean, errors: Array}}
     */
    validateSyntax(expression) {
        const errors = [];

        if (!expression || typeof expression !== 'string') {
            errors.push({
                type: ExpressionErrorType.SYNTAX_ERROR,
                message: 'Expressao invalida',
            });
            return { valid: false, errors };
        }

        const trimmed = expression.trim();

        if (trimmed.length === 0) {
            errors.push({
                type: ExpressionErrorType.SYNTAX_ERROR,
                message: 'Expressao vazia',
            });
            return { valid: false, errors };
        }

        // Validar parenteses balanceados
        const parenthesesError = this.validateParentheses(trimmed);
        if (parenthesesError) {
            errors.push(parenthesesError);
        }

        // Validar funcoes
        const functionErrors = this.validateFunctions(trimmed);
        errors.push(...functionErrors);

        // Validar variaveis
        const variableErrors = this.validateVariables(trimmed);
        errors.push(...variableErrors);

        return {
            valid: errors.length === 0,
            errors,
        };
    }

    /**
     * Valida parenteses balanceados
     * @param {string} expression
     * @returns {Object|null} Erro ou null
     */
    validateParentheses(expression) {
        let count = 0;

        for (let i = 0; i < expression.length; i++) {
            if (expression[i] === '(') {
                count++;
            } else if (expression[i] === ')') {
                count--;
                if (count < 0) {
                    return {
                        type: ExpressionErrorType.SYNTAX_ERROR,
                        message: 'Parenteses nao balanceados',
                        position: { column: i },
                    };
                }
            }
        }

        if (count !== 0) {
            return {
                type: ExpressionErrorType.SYNTAX_ERROR,
                message: 'Parenteses nao balanceados',
            };
        }

        return null;
    }

    /**
     * Valida funcoes na expressao
     * @param {string} expression
     * @returns {Array} Array de erros
     */
    validateFunctions(expression) {
        const errors = [];
        const availableFunctions = this.getAvailableFunctions();
        const functionNames = availableFunctions.map((f) => f.name);

        // Regex para encontrar chamadas de funcao
        const functionRegex = /([A-Z_]+)\s*\(/g;
        let match;

        while (true) {
            match = functionRegex.exec(expression);
            if (match === null) {
                break;
            }

            const funcName = match[1];

            if (!functionNames.includes(funcName)) {
                errors.push({
                    type: ExpressionErrorType.UNKNOWN_FUNCTION,
                    message: ERROR_MESSAGES.UNKNOWN_FUNCTION.replace('{function}', funcName),
                    suggestion: `Funcoes disponiveis: ${functionNames.join(', ')}`,
                });
            }
        }

        return errors;
    }

    /**
     * Valida variaveis na expressao
     * @param {string} expression
     * @returns {Array} Array de erros
     */
    validateVariables(expression) {
        const errors = [];
        const availableVariables = this.getAvailableVariables();
        const variableNames = availableVariables.map((v) => v.name);

        // Regex para encontrar variaveis (palavras em maiusculas que nao sao funcoes)
        const variableRegex = /\b([A-Z_]+)\b(?!\s*\()/g;
        let match;

        while (true) {
            match = variableRegex.exec(expression);
            if (match === null) {
                break;
            }

            const varName = match[1];

            // Ignorar operadores e numeros
            if (['AND', 'OR', 'NOT'].includes(varName)) {
                continue;
            }

            if (!variableNames.includes(varName)) {
                errors.push({
                    type: ExpressionErrorType.UNKNOWN_VARIABLE,
                    message: ERROR_MESSAGES.UNKNOWN_VARIABLE.replace('{variable}', varName),
                    suggestion: `Variaveis disponiveis: ${variableNames.join(', ')}`,
                });
            }
        }

        return errors;
    }

    /**
     * Retorna funcoes disponiveis
     * @returns {Array}
     */
    getAvailableFunctions() {
        return [
            {
                name: 'SUM',
                description: 'Soma de valores',
                signature: 'SUM(valores)',
                category: 'aggregation',
            },
            {
                name: 'AVG',
                description: 'Media de valores',
                signature: 'AVG(valores)',
                category: 'aggregation',
            },
            {
                name: 'MIN',
                description: 'Valor minimo',
                signature: 'MIN(valores)',
                category: 'aggregation',
            },
            {
                name: 'MAX',
                description: 'Valor maximo',
                signature: 'MAX(valores)',
                category: 'aggregation',
            },
            {
                name: 'COUNT',
                description: 'Contagem de valores',
                signature: 'COUNT(valores)',
                category: 'aggregation',
            },
            {
                name: 'LAST_N_DAYS',
                description: 'Total dos ultimos N dias',
                signature: 'LAST_N_DAYS(n)',
                category: 'period',
            },
            {
                name: 'WEEK_AGO',
                description: 'Total da semana anterior (7 dias atras)',
                signature: 'WEEK_AGO()',
                category: 'period',
            },
            {
                name: 'MONTH_AGO',
                description: 'Total do mes anterior (30 dias atras)',
                signature: 'MONTH_AGO()',
                category: 'period',
            },
            {
                name: 'YEAR_AGO',
                description: 'Total do ano anterior (365 dias atras)',
                signature: 'YEAR_AGO()',
                category: 'period',
            },
            {
                name: 'GROWTH',
                description: 'Crescimento percentual entre dois valores',
                signature: 'GROWTH(atual, anterior)',
                category: 'comparison',
            },
            {
                name: 'VARIANCE',
                description: 'Variacao absoluta entre dois valores',
                signature: 'VARIANCE(atual, anterior)',
                category: 'comparison',
            },
            {
                name: 'PERCENT_CHANGE',
                description: 'Variacao percentual entre dois valores',
                signature: 'PERCENT_CHANGE(atual, anterior)',
                category: 'comparison',
            },
            {
                name: 'IF',
                description: 'Condicional: retorna um valor se condicao verdadeira, outro se falsa',
                signature: 'IF(condicao, valorVerdadeiro, valorFalso)',
                category: 'conditional',
            },
        ];
    }

    /**
     * Retorna variaveis disponiveis
     * @returns {Array}
     */
    getAvailableVariables() {
        return [
            {
                name: 'TODAY',
                description: 'Vendas do dia atual',
                type: 'number',
            },
            {
                name: 'YESTERDAY',
                description: 'Vendas de ontem',
                type: 'number',
            },
            {
                name: 'CURRENT_WEEK',
                description: 'Vendas da semana atual (ultimos 7 dias)',
                type: 'number',
            },
            {
                name: 'CURRENT_MONTH',
                description: 'Vendas do mes atual (ultimos 30 dias)',
                type: 'number',
            },
            {
                name: 'AVERAGE',
                description: 'Media de vendas por dia',
                type: 'number',
            },
            {
                name: 'BEST_DAY',
                description: 'Quantidade do melhor dia',
                type: 'number',
            },
        ];
    }

    /**
     * Retorna assinatura de uma funcao
     * @param {string} name
     * @returns {string}
     */
    getFunctionSignature(name) {
        const func = this.getAvailableFunctions().find((f) => f.name === name);
        return func ? func.signature : '';
    }

    /**
     * Avalia uma expressao com o contexto do produto
     * @param {string} expression
     * @param {Object} context - Contexto do produto
     * @returns {any} Resultado da avaliacao
     */
    evaluate(expression, context) {
        // Verificar cache
        const cacheKey = `${expression}:${context.itemId}`;
        if (this.cache.has(cacheKey)) {
            return this.cache.get(cacheKey);
        }

        try {
            // Validar sintaxe
            const validation = this.validateSyntax(expression);
            if (!validation.valid) {
                throw new Error(validation.errors[0]?.message || 'Erro de sintaxe');
            }

            // Sanitizar expressao
            const sanitized = this.sanitize(expression);

            // Mapear variaveis
            const variables = this.mapVariables(context);

            // Criar contexto de execucao
            const executionContext = {
                ...variables,
                context,
                // Funcoes
                SUM: (...args) => this._SUM(...args),
                AVG: (...args) => this._AVG(...args),
                MIN: (...args) => this._MIN(...args),
                MAX: (...args) => this._MAX(...args),
                COUNT: (...args) => this._COUNT(...args),
                LAST_N_DAYS: (n) => this._LAST_N_DAYS(context, n),
                WEEK_AGO: () => this._WEEK_AGO(context),
                MONTH_AGO: () => this._MONTH_AGO(context),
                YEAR_AGO: () => this._YEAR_AGO(context),
                GROWTH: (a, b) => this._GROWTH(a, b),
                VARIANCE: (a, b) => this._VARIANCE(a, b),
                PERCENT_CHANGE: (a, b) => this._PERCENT_CHANGE(a, b),
                IF: (cond, t, f) => this._IF(cond, t, f),
            };

            // Executar com timeout
            const result = this.executeWithTimeout(sanitized, executionContext);

            // Cachear resultado
            this.cache.set(cacheKey, result);

            return result;
        } catch (error) {
            console.error('Erro ao avaliar expressao:', error);
            throw error;
        }
    }

    /**
     * Sanitiza expressao para prevenir codigo malicioso
     * @param {string} expression
     * @returns {string}
     */
    sanitize(expression) {
        // Remover comentarios
        const sanitized = expression.replace(/\/\*[\s\S]*?\*\/|\/\/.*/g, '');

        // Bloquear palavras-chave perigosas
        const dangerousKeywords = [
            'eval',
            'Function',
            'setTimeout',
            'setInterval',
            'window',
            'document',
            'global',
            'process',
            'require',
            'import',
            'export',
            '__proto__',
            'constructor',
            'prototype',
        ];

        dangerousKeywords.forEach((keyword) => {
            const regex = new RegExp(`\\b${keyword}\\b`, 'gi');
            if (regex.test(sanitized)) {
                throw new Error(`Palavra-chave nao permitida: ${keyword}`);
            }
        });

        return sanitized;
    }

    /**
     * Executa expressao com timeout
     * @param {string} expression
     * @param {Object} context
     * @returns {any}
     */
    executeWithTimeout(expression, context) {
        // Criar funcao com escopo limitado
        // eslint-disable-next-line no-new-func
        const func = new Function(
            ...Object.keys(context),
            `
      'use strict';
      try {
        return ${expression};
      } catch (error) {
        throw new Error('Erro na expressao: ' + error.message);
      }
    `,
        );

        // Executar
        const result = func(...Object.values(context));

        return result;
    }

    /**
     * Limpa o cache
     */
    clearCache() {
        this.cache.clear();
    }

    /**
     * Implementacoes das funcoes
     */

    // Funcoes agregadas
    _SUM(...values) {
        return values.reduce((sum, val) => sum + (Number(val) || 0), 0);
    }

    _AVG(...values) {
        if (values.length === 0) {
            return 0;
        }
        const sum = this._SUM(...values);
        return sum / values.length;
    }

    _MIN(...values) {
        if (values.length === 0) {
            return 0;
        }
        return Math.min(...values.map((v) => Number(v) || 0));
    }

    _MAX(...values) {
        if (values.length === 0) {
            return 0;
        }
        return Math.max(...values.map((v) => Number(v) || 0));
    }

    _COUNT(...values) {
        return values.length;
    }

    // Funcoes de periodo
    _LAST_N_DAYS(context, n) {
        const days = Number(n);
        if (isNaN(days) || days <= 0) {
            throw new Error('LAST_N_DAYS requer um numero positivo');
        }

        // Usar dados ja calculados se disponivel
        const key = `last${days}d`;
        if (context[key] !== undefined) {
            return context[key];
        }

        // Calcular manualmente se necessario
        if (context.getSalesForRange) {
            return context.getSalesForRange(0, days - 1);
        }

        return 0;
    }

    _WEEK_AGO(context) {
        // Vendas de 7-14 dias atras
        if (context.getSalesForRange) {
            return context.getSalesForRange(7, 13);
        }
        return 0;
    }

    _MONTH_AGO(context) {
        // Vendas de 30-60 dias atras
        if (context.getSalesForRange) {
            return context.getSalesForRange(30, 59);
        }
        return 0;
    }

    _YEAR_AGO(context) {
        // Vendas de 365-730 dias atras
        if (context.getSalesForRange) {
            return context.getSalesForRange(365, 729);
        }
        return 0;
    }

    // Funcoes de comparacao
    _GROWTH(current, previous) {
        const curr = Number(current) || 0;
        const prev = Number(previous) || 0;

        if (prev === 0) {
            return curr > 0 ? 100 : 0;
        }

        return ((curr - prev) / prev) * 100;
    }

    _VARIANCE(current, previous) {
        const curr = Number(current) || 0;
        const prev = Number(previous) || 0;
        return curr - prev;
    }

    _PERCENT_CHANGE(current, previous) {
        return this._GROWTH(current, previous);
    }

    // Condicional
    _IF(condition, trueValue, falseValue) {
        return condition ? trueValue : falseValue;
    }

    /**
     * Mapeia variaveis do contexto
     * @param {Object} context
     * @returns {Object}
     */
    mapVariables(context) {
        return {
            TODAY: context.today || 0,
            YESTERDAY: context.yesterday || 0,
            CURRENT_WEEK: context.last7d || 0,
            CURRENT_MONTH: context.last30d || 0,
            AVERAGE: context.average || 0,
            BEST_DAY: context.bestDay?.quantity || 0,
        };
    }
}

export default ExpressionEngine;
